import React, { useEffect, useState } from "react";
import ResponsiveDataManagement from "../../../Components/Common/ResponsiveManagementLayout";
import CustomerMetaFieldService from "../../../services/OrderingServices/CustomerMetaFieldService";
import CustomerMetaFieldForm from "../component/CustomerMetaFieldForm";
import CustomerMetaFieldViewModal from "../component/CustomerMetaFieldViewModal";
import DeleteModal from "../../../Components/Common/DeleteModal";
import {
  Card,
  CardBody,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import StatusBadge from "../../../Components/Common/Badge";

const CustomerMetaField = () => {
  const [unitList, setUnitList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  // Summary stats
  const [totalMetaFields, setTotalMetaFields] = useState(0);
  const [activeMetaFields, setActiveMetaFields] = useState(0);
  const [inactiveMetaFields, setInactiveMetaFields] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");

  const fetchUnitList = async () => {
    try {
      setLoading(true);
      const response = await CustomerMetaFieldService.getList();
      const items = Array.isArray(response) ? response : response?.items || [];
      setUnitList(items);
      setTotalMetaFields(items.length);
      setActiveMetaFields(items.filter((i) => i.isActive).length);
      setInactiveMetaFields(items.filter((i) => !i.isActive).length);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchUnitList();
  }, []);

  const handleAdd = () => setAddModal(true);
  const handleEdit = (unit) => {
    setSelectedUnit(unit);
    setAddModal(true);
  };
  const handleView = (unit) => {
    setSelectedUnit(unit);
    setViewModal(true);
  };
  const handleDeleteClick = (unit) => {
    setSelectedUnit(unit);
    setDeleteModal(true);
  };
  const handleDelete = async () => {
    if (selectedUnit) {
      try {
        await CustomerMetaFieldService.delete(selectedUnit.id || selectedUnit);
        setUnitList((prev) =>
          prev.filter((unit) => unit.id !== (selectedUnit.id || selectedUnit))
        );
        setDeleteModal(false);
        setSelectedUnit(null);
      } catch (error) {
        console.error(error);
      }
    }
  };

  const columns = [
    {
      name: "S.N",
      selector: (row, index) => (currentPage - 1) * perPage + index + 1,
      sortable: false,
      width: "75px",
    },
    {
      name: "Code",
      selector: (row) => row.code,
      sortable: true,
    },
    {
      name: "Title",
      selector: (row) => row.title,
      sortable: true,
    },
    {
      name: "Description",
      selector: (row) => row.description,
    },
    {
      name: "Status",
      cell: (row) => (
        <StatusBadge status={row.isActive} type="status" pill={true} />
      ),
      sortable: true,
    },
  ];

  const stats = [
    {
      title: "Total Meta Fields",
      value: totalMetaFields,
      color: "#0d6efd",
      icon: "bx bx-list-ul",
    },
    {
      title: "Active Meta Fields",
      value: activeMetaFields,
      color: "#28a745",
      icon: "bx bx-check-circle",
    },
    {
      title: "Inactive Meta Fields",
      value: inactiveMetaFields,
      color: "#ffc107",
      icon: "bx bx-block",
    },
  ];

  const filteredUnitList = unitList.filter((unit) => {
    if (!statusFilter) return true;
    return statusFilter === "active" ? unit.isActive : !unit.isActive;
  });

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
  ];

  const handleStatusFilterChange = (value) => {
    setStatusFilter(value);
  };

  return (
    <>
      <ResponsiveDataManagement
        title="Customer Meta Fields"
        pageTitle="User Management"
        mobileCardTitle="Customer Meta Fields List"
        data={filteredUnitList}
        columns={columns}
        stats={stats}
        searchKeys={["code", "title", "description", "isActive"]}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onView={handleView}
        onDelete={handleDelete}
        onSelect={handleStatusFilterChange}
        selectOptions={statusOptions}
        selectValue={statusFilter}
        selectPlaceholder="All Status"
        mobileCardRenderer={(item) => (
          <div className="mb-3">
            <Card className="shadow-sm rounded-3 border-0 border-start border-4 border-success">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">
                    <h6 className="fw-bold text-primary mb-1">{item.title}</h6>
                    <p className="mb-1 text-muted small">
                      Code: {item.code || "-"}
                    </p>
                    <p className="mb-1 text-muted small">
                      {item.description || "-"}
                    </p>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                      <span
                        className={`badge ${
                          item.isActive ? "bg-success" : "bg-danger"
                        }`}
                      >
                        {item.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>

                  {/* Mobile dropdown actions */}
                  <Dropdown
                    isOpen={openDropdownId === item.id}
                    toggle={() =>
                      setOpenDropdownId(
                        openDropdownId === item.id ? null : item.id
                      )
                    }
                    direction="down"
                  >
                    <DropdownToggle
                      color="light"
                      className="btn-sm rounded-circle shadow-sm"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <i className="ri-more-2-fill" />
                    </DropdownToggle>

                    <DropdownMenu end>
                      {/* <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleView(item);
                          setOpenDropdownId(null);
                        }}
                      >
                        <i className="bx bx-show me-2" />
                        View
                      </DropdownItem>
                      <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEdit(item);
                          setOpenDropdownId(null);
                        }}
                      >
                        <i className="ri-edit-line me-2" />
                        Edit
                      </DropdownItem> */}

                      {/* <DropdownItem
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(item);
                          setOpenDropdownId(null);
                        }}
                      >
                        <i className="ri-delete-bin-line me-2" />
                        Delete
                      </DropdownItem> */}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </CardBody>
            </Card>
          </div>
        )}
        loading={loading}
        emptyStateMessage="No customer meta fields found"
        onPaginationChange={({ page, perPage }) => {
          setCurrentPage(page);
          setPerPage(perPage);
        }}
      />

      <CustomerMetaFieldForm
        modal={addModal}
        toggleModal={() => {
          setAddModal(false);
          setSelectedUnit(null);
        }}
        setUnitList={setUnitList}
        unitList={unitList}
        selectedUnit={selectedUnit}
        fetchUnit={fetchUnitList}
      />

      {/* <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => setDeleteModal(false)}
        selectedItem={selectedUnit}
      /> */}

      <CustomerMetaFieldViewModal
        isOpen={viewModal}
        toggle={() => {
          setViewModal(false);
          setSelectedUnit(null);
        }}
        selectedId={selectedUnit?.id}
      />
    </>
  );
};

export default CustomerMetaField;
